import React, { useState, useEffect } from 'react';
import { channelApi, packageApi, bpProductNewApi } from '../config/api';

const MEMBER_CODE_MAPPING = {
  'api': 'Injourney Airports',
  'ias': 'Injourney Aviation Services',
  'twc': 'Injourney Destination Management',
  'hin': 'Injourney Hospitality',
  'itdc': 'Injourney Tourism Development',
  'srn': 'Injourney Retail'
};
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FiRefreshCw, FiShoppingBag, FiGlobe, FiCheckCircle, FiActivity, FiSearch, FiBox, FiTag } from 'react-icons/fi';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function PackageMonitor() {
  const [transactions, setTransactions] = useState([]);
  const [publishedItems, setPublishedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChannel, setFilterChannel] = useState('ALL');
  const [unpublishingId, setUnpublishingId] = useState(null);
  const { user } = useAuth();

  const fetchPublishedItems = async () => {
    try {
      const [pkgRes, prodRes] = await Promise.all([
        packageApi.get('/list'),
        bpProductNewApi.get('/list'),
      ]);
      const packages = (pkgRes.data?.data || []).filter(p => p.PACKAGE_APISTATUS === 'published').map(p => ({
        id: p.PACKAGE_ID, type: 'package',
        name: p.PACKAGE_NAME || p.PACKAGE_CODE || `Package #${p.PACKAGE_ID}`,
        code: p.PACKAGE_CODE,
        price: parseFloat(p.PACKAGE_DISCOUNTPRICE || p.PACKAGE_BASEPRICE) || 0, quota: p.PACKAGE_KUOTA || 0,
        member: p.PACKAGE_PICMEMBER ? (MEMBER_CODE_MAPPING[p.PACKAGE_PICMEMBER.toLowerCase()] || p.PACKAGE_PICMEMBER) : '-', status: 'Published',
      }));
      const products = (prodRes.data?.data || []).filter(p => p.BPPRODUCT_APISTATUS === 'published').map(p => ({
        id: p.BPPRODUCT_ID, type: 'product', name: p.BPPRODUCT_NAME, code: p.BPPRODUCT_CODE,
        price: parseFloat(p.BPPRODUCT_PUBLISHPRICE || p.BPPRODUCT_CORPPRICE) || 0, quota: p.BPPRODUCT_QUANTITY || 0,
        member: p.MEMBER_CODE || '-', status: 'Published',
      }));
      setPublishedItems([...packages, ...products]);
    } catch (err) { console.error('Error fetching published items:', err); }
  };

  const handleUnpublish = async (item) => {
    setUnpublishingId(`${item.type}-${item.id}`);
    try {
      const api = item.type === 'package' ? packageApi : bpProductNewApi;
      const res = await api.post(`/unpublish/${item.id}`);
      if (res.data?.status === 200) {
        setPublishedItems(prev => prev.filter(i => !(i.type === item.type && i.id === item.id)));
        toast.success(`"${item.name}" berhasil di-unpublish.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal unpublish.');
    } finally {
      setUnpublishingId(null);
    }
  };

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (user?.admin_status === 'admin_member' && user?.organization_code) {
        params.member_code = user.organization_code.toLowerCase();
      }
      const response = await channelApi.get('/admin/transactions', { params });
      if (response.data?.status === 200) {
        setTransactions(response.data.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memuat data monitoring paket.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchPublishedItems();
  }, [user?.organization_code, user?.admin_status]);

  const formatIDR = (num) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(num);
  };

  // Computed values from real data
  const channelCounts = {};
  transactions.forEach(tx => {
    channelCounts[tx.channel_name] = (channelCounts[tx.channel_name] || 0) + 1;
  });
  const topChannelEntry = Object.entries(channelCounts).sort((a, b) => b[1] - a[1])[0];
  const topChannelName = topChannelEntry ? topChannelEntry[0] : '-';
  const topChannelPct = topChannelEntry ? Math.round((topChannelEntry[1] / transactions.length) * 100) : 0;

  // Dynamic channels list for filter buttons
  const activeChannelNames = [...new Set(transactions.map(tx => tx.channel_name).filter(Boolean))];
  const filteredTransactions = transactions.filter(tx => {
    const matchesSearch = 
      tx.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.invoiceCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.packageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.passengers.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesChannel = 
      filterChannel === 'ALL' || 
      tx.channel_name.toLowerCase().includes(filterChannel.toLowerCase());

    return matchesSearch && matchesChannel;
  });

  return (
    <div className="flex flex-col gap-6">
      
      {/* Header action bar */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-800">
            {user?.admin_status === 'admin_member'
              ? `Monitoring Paket - ${String(user?.organization_code || '').toUpperCase()}`
              : 'Monitoring Paket Konsolidasi (Holding)'}
          </h1>
          <p className="text-sm text-slate-500">
            {user?.admin_status === 'admin_member'
              ? 'Pantau volume penjualan dan kontribusi paket bundling terafiliasi secara real-time.'
              : 'Pantau seluruh transaksi pembelian paket bundling dari berbagai saluran platform OTA secara terpusat.'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchTransactions} className="gap-2 self-start md:self-auto">
          <FiRefreshCw className={isLoading ? 'animate-spin' : ''} />
          Refresh Data
        </Button>
      </div>

      {/* Published Products & Packages */}
      <Card className="border-0 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-bold text-slate-700">
              Published Products & Packages ({publishedItems.length})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchPublishedItems}><FiRefreshCw /></Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-bold">Name</TableHead>
                <TableHead className="font-bold">Type</TableHead>
                <TableHead className="font-bold">Code</TableHead>
                <TableHead className="font-bold">Member</TableHead>
                <TableHead className="font-bold">Price</TableHead>
                <TableHead className="font-bold">Status</TableHead>
                <TableHead className="font-bold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {publishedItems.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400">Belum ada product/package yang dipublish ke API.</TableCell></TableRow>
              ) : publishedItems.map(item => (
                <TableRow key={`${item.type}-${item.id}`}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell><Badge variant={item.type === 'package' ? 'default' : 'secondary'}>{item.type === 'package' ? 'Package' : 'Product'}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{item.code}</TableCell>
                  <TableCell className="text-xs">{item.member}</TableCell>
                  <TableCell className="font-mono text-xs">Rp {item.price.toLocaleString('id-ID')}</TableCell>
                  <TableCell><Badge className="bg-green-100 text-green-700">{item.status}</Badge></TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      disabled={unpublishingId === `${item.type}-${item.id}`}
                      onClick={() => handleUnpublish(item)}
                    >
                      {unpublishingId === `${item.type}-${item.id}` ? 'Loading...' : 'Unpublish'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Analytics widgets */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-[#ECFDF5] p-3 text-[#059669]">
                <FiShoppingBag className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Paket Dipesan</p>
                <h3 className="text-2xl font-bold text-slate-800">{transactions.length} Pesanan</h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-[#EFF6FF] p-3 text-[#2563EB]">
                <FiGlobe className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Mitra Terbesar</p>
                <h3 className="text-lg font-bold text-slate-800 truncate max-w-[180px]">
                  {transactions.length > 0 ? `${topChannelName} (${topChannelPct}%)` : '-'}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-amber-50 p-3 text-amber-600">
                <FiActivity className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Omset Penjualan Paket</p>
                <h3 className="text-xl font-black text-slate-800">
                  {formatIDR(transactions.reduce((sum, tx) => sum + (tx.total_price || 0), 0))}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-green-50 p-3 text-green-600">
                <FiCheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Channel Aktif</p>
                <h3 className="text-2xl font-bold text-slate-800">{activeChannelNames.length} Channel</h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Table Card */}
      <Card className="border-0 bg-white shadow-sm">
        <div className="p-6 pb-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <h2 className="text-base font-bold text-slate-700">Daftar Reservasi Paket Bundling</h2>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Search Bar */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input 
                  type="text"
                  placeholder="Cari booking, paket..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg border border-slate-200 pl-9 pr-4 py-1.5 text-xs focus:border-brand-500 focus:outline-none w-[220px]"
                />
              </div>

              {/* Channel Filter buttons — dynamic from active channels */}
              <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs">
                <button
                  onClick={() => setFilterChannel('ALL')}
                  className={`px-3 py-1 rounded-md font-semibold transition-all ${filterChannel === 'ALL' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  Semua
                </button>
                {activeChannelNames.map(ch => (
                  <button
                    key={ch}
                    onClick={() => setFilterChannel(ch)}
                    className={`px-3 py-1 rounded-md font-semibold transition-all ${filterChannel === ch ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    {ch}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-bold w-[120px]">Kode Booking</TableHead>
                <TableHead className="font-bold">Mitra Saluran</TableHead>
                <TableHead className="font-bold">Paket Liburan</TableHead>
                <TableHead className="font-bold">Tanggal Perjalanan</TableHead>
                <TableHead className="font-bold">Total Pembayaran</TableHead>
                <TableHead className="font-bold">Jumlah Pax</TableHead>
                <TableHead className="font-bold text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-slate-400">Memuat data paket...</TableCell>
                </TableRow>
              ) : filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-slate-400">Tidak ada reservasi paket yang cocok.</TableCell>
                </TableRow>
              ) : (
                filteredTransactions.map((tx) => (
                  <TableRow key={tx.id} className="hover:bg-slate-50/50">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-800">{tx.id}</span>
                        <span className="text-[10px] font-mono text-slate-400 mt-0.5">{tx.invoiceCode}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-700">{tx.channel_name}</span>
                        <span className="text-[9px] text-slate-400">
                          {new Date(tx.bookingTime).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-slate-800 text-xs">
                      {tx.packageName}
                      <span className="block font-mono text-[9px] text-slate-400 mt-0.5">Kode: {tx.packageCode}</span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 font-medium">
                      {new Date(tx.travelDate).toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </TableCell>
                    <TableCell className="font-black text-slate-800 text-xs">
                      {formatIDR(tx.total_price)}
                    </TableCell>
                    <TableCell className="text-xs font-semibold text-slate-600">
                      {tx.passengers?.length || 0} Pax
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15 border-0">
                        {tx.paymentStatus || 'Paid'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

    </div>
  );
}
